import type { DeliveryJob, PickupJob, Task, TaskJob, TaskProof, TaskUnitsControl } from '../task/task.interface';
import { TASK_JOB_STATUS, TASK_JOB_TYPE, TASK_STATUS, TASK_TYPE } from '../task/task.interface';
import { SITE_TYPE } from '../site/site.interface';
import { baseDocumentFactory } from './base-document.factory';
import { ObjectFactory } from './object.factory';

const DEFAULT_DELIVERY_JOB: DeliveryJob = {
  data: {
    site: {
      code: 'DEL01',
      color: '#000000',
      id: 'aabbccddeeff001122334455',
      name: 'Delivery Site',
      type: SITE_TYPE.WAREHOUSE,
    },
  },
  id: 'aaaaaaaaaaaaaaaaaaaaaaaa',
  sortIndex: 1,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.DELIVERY,
};

const DEFAULT_PICKUP_JOB: PickupJob = {
  data: {
    durationTime: 10,
    site: {
      code: 'PICK01',
      color: '#000000',
      id: 'bbccddeeff001122334455aa',
      name: 'Pickup Site',
      type: SITE_TYPE.WAREHOUSE,
    },
  },
  id: 'bbbbbbbbbbbbbbbbbbbbbbbb',
  sortIndex: 0,
  status: TASK_JOB_STATUS.TO_DO,
  type: TASK_JOB_TYPE.PICKUP,
};

export class DeliveryJobFactory extends ObjectFactory<DeliveryJob> {
  protected getDefaults(): DeliveryJob {
    return DEFAULT_DELIVERY_JOB;
  }
}

export const deliveryJobFactory = new DeliveryJobFactory();

export class PickupJobFactory extends ObjectFactory<PickupJob> {
  protected getDefaults(): PickupJob {
    return DEFAULT_PICKUP_JOB;
  }
}

export const pickupJobFactory = new PickupJobFactory();

export class TaskFactory extends ObjectFactory<Task> {
  protected getDefaults(): Task {
    return {
      ...baseDocumentFactory.build(),
      jobs: [DEFAULT_DELIVERY_JOB],
      order: {
        id: 'bbccddeeff001122334455aa',
        referenceNumber: 'REF-001',
        tags: [],
        totalHandlingUnitQuantity: undefined,
      },
      site: {
        code: 'SITE-01',
        color: '#FF5733',
        id: 'ccddeeff001122334455aabb',
        name: 'Main Site',
        type: SITE_TYPE.WAREHOUSE,
      },
      spaceId: 'ddeeff001122334455aabbcc',
      status: TASK_STATUS.ARRIVED_AT_DELIVERY,
      type: TASK_TYPE.DELIVERY,
      shipperId: 'eeff001122334455aabbccdd',
    };
  }

  /** Builds a task with both a pickup job (sortIndex 0) and a delivery job (sortIndex 1). */
  buildWithBothJobs(overrides: Partial<Task> = {}): Task {
    return this.build({
      ...overrides,
      jobs: [DEFAULT_PICKUP_JOB, DEFAULT_DELIVERY_JOB],
    });
  }
}

export const taskFactory = new TaskFactory();

/** Returns a copy of a job with proofs attached — for tests exercising Task.jobs[].proofs. */
export function withProofs<T extends TaskJob>(job: T, proofs: TaskProof[]): T {
  return { ...job, proofs };
}

/** Returns a copy of a task with `unitsControl` attached — for tests exercising driver load/unload controls. */
export function withUnitsControl<T extends Task>(task: T, unitsControl: TaskUnitsControl): T {
  return { ...task, unitsControl };
}
